// In ArrayInput.jsx

import { useState, useEffect, useRef } from "react";
import { MdOutlineAdd, MdDelete } from "react-icons/md";

import Input from ".";
import SelectInput from "./selectInput";

const ArrayInput = ({ schema, uischema, onChangeFunc, initialData = [] }) => {
  const [items, setItems] = useState([]);
  const properties = schema?.items?.properties;

  const defaultTierNames = ["Tier 1", "Tier 2", "Tier 3", "Tier 4", "Tier 5"];

  const isInitialMount = useRef(true);

  // Initialize items only once (or when schema changes)
  useEffect(() => {
    if (items.length === 0) {
      if (initialData && initialData.length > 0) {
        const clonedInitialData = JSON.parse(JSON.stringify(initialData));
        setItems(clonedInitialData);
      } else {
        // start with an empty list — user must click "Add"
        setItems([]);
      }
    }
  }, [schema?.name]);

  // Notify parent of changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (schema && onChangeFunc) {
      onChangeFunc(items);
    }
  }, [items, schema]);

  const addItem = () => {
    const newItem = {};
    if (schema?.name === "Tiered Price Groups") {
      const nextTierIndex = items.length;
      newItem.tierName =
        defaultTierNames[nextTierIndex] || `Tier ${nextTierIndex + 1}`;
      newItem.priceGroups = [];
    }
    setItems((prevItems) => [...prevItems, newItem]);
  };

  const removeItem = (index) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const mapOptions = (options, key) => {
    return options.map((element) => {
      if (
        typeof element === "object" &&
        element !== null &&
        "name" in element &&
        "id" in element
      ) {
        return { label: element.name, value: element.id };
      } else if (key === "currency") {
        return { label: element, value: element.split(" - ")[0] };
      } else if (key === "tierName") {
        return { label: element, value: element };
      } else {
        return { label: element, value: element.toLowerCase() };
      }
    });
  };

  const handleChange = (index, key, value) => {
    setItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[index] = { ...newItems[index], [key]: value };
      return newItems;
    });
  };

  const handleNestedArrayChange = (
    parentIndex,
    nestedKey,
    newNestedArrayData
  ) => {
    setItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[parentIndex] = {
        ...newItems[parentIndex],
        [nestedKey]: newNestedArrayData,
      };
      return newItems;
    });
  };

  const capitalizeFirstLetter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <div className="p-3 border border-gray-300 rounded my-3">
      <div className="flex justify-between items-center">
        <div className="font-bold">
          {schema?.title || schema?.name || "Items"}
        </div>
        <button
          onClick={addItem}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center space-x-1"
        >
          <MdOutlineAdd /> Add{" "}
          {schema?.name
            ? schema.name.split(" ").slice(0, -1).join(" ")
            : "Item"}
        </button>
      </div>

      {items.map((item, index) => (
        <div
          key={index}
          className="flex flex-col space-y-2 mx-3 mt-4 p-3 border border-gray-300 rounded relative"
        >
          {items.length > 0 && (
            <button
              onClick={() => removeItem(index)}
              className="absolute top-0 right-0 bg-red-500 text-white px-2 py-1 rounded hover:bg-red-700"
            >
              <MdDelete />
            </button>
          )}
          {properties &&
            Object.entries(properties).map(([key, value]) => (
              <div key={key} className="flex-1 w-full">
                {/* Enum Strings -> SelectInput */}
                {value?.type === "string" && value?.enum && (
                  <div>
                    <div className="font-medium">
                      {capitalizeFirstLetter(key)}
                    </div>
                    <SelectInput
                      label={key}
                      name={`${key}-${index}`}
                      options={mapOptions(value.enum, key)}
                      className="!h-16"
                      value={item[key] || ""}
                      data={item[key] || ""}
                      onChange={(selectedOption) =>
                        handleChange(index, key, selectedOption)
                      }
                    />
                  </div>
                )}
                {/* Regular Strings -> Input */}
                {value?.type === "string" && !value?.enum && (
                  <div>
                    <div className="font-medium">
                      {capitalizeFirstLetter(key)}
                    </div>
                    <Input
                      label={key}
                      name={`${key}-${index}`}
                      value={item[key] || ""}
                      data={item[key] || ""}
                      onChange={(e) => handleChange(index, key, e)}
                    />
                  </div>
                )}
                {/* Nested Arrays */}
                {value?.type === "array" && (
                  <div className="mt-4 p-2 border border-blue-200 rounded">
                    <div className="font-bold mb-2">
                      {value?.title ||
                        value?.name ||
                        capitalizeFirstLetter(key)}
                    </div>
                    <ArrayInput
                      schema={value}
                      uischema={uischema}
                      initialData={item[key] || []}
                      onChangeFunc={(newNestedArrayData) =>
                        handleNestedArrayChange(index, key, newNestedArrayData)
                      }
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      ))}
    </div>
  );
};

export default ArrayInput;
